import React, { FC } from 'react';
import NextLink from 'next/link';
import classNames from 'classnames';
import { useAction, useAtom } from '@reatom/npm-react';
import { Icon, Loader, Tooltip } from '@whitesharx/starfall-frontend-ui-kit';
import {
  GameMode,
  usePlayerQuery,
  resultGetterMap,
  t,
  Modal,
  GameResultTitle,
  useModal,
  Play,
  hasVIP,
  GAME_MODE_TITLES,
  Routes,
} from '@whitesharx/starfall-frontend-core';
import {
  chooseButtonStyles,
  defaultColorStyles,
  infoStyles,
  iconStrokeStyles,
  listItemStyles,
  containerStyles,
  defeatColorStyles,
  modeStyles,
  victoryColorStyles,
  resultTitleStyles,
  resultContainerStyles,
  infoIconContainerStyles,
  infoIconStyles,
  vipInfoStyles,
  dateStyles,
  infoItemTextStyles,
  infoModalContentStyles,
  infoItemStyles,
  iconSizeStyles,
  wrapperStyles,
  noDataTextStyles,
  flexCenterStyles,
  modalContentStyles,
  chooseGameInputStyles,
} from './styles.css';
import { chooseGameAction, chosenGamesCountAtom, getGamesAction } from './model';

type GameModeWithTimers = Exclude<GameMode, GameMode.GAMEWITHOUTTIMERS>;
type GameResult = Exclude<GameResultTitle, GameResultTitle.canceled>;

const MAX_FAVORITE_GAMES_COUNT_WITHOUT_VIP = 3;

const RESULT_TITLE_CLASSNAME: Record<GameResult, string> = {
  [GameResultTitle.victory]: victoryColorStyles,
  [GameResultTitle.defeat]: defeatColorStyles,
  [GameResultTitle.draw]: defaultColorStyles,
  [GameResultTitle.stalemate]: defaultColorStyles,
};

type Props = {
  onRequestClose: () => void;
};

const GameArchiveModal: FC<Props> = ({ onRequestClose }) => {
  const { isOpen: isOpenInfoModal, close: closeInfoModa, open: openInfoModal } = useModal();

  const { data: playerData } = usePlayerQuery();
  const [games] = useAtom(getGamesAction.dataAtom);
  const [status] = useAtom(getGamesAction.statusesAtom);
  const [chosenGamesCount] = useAtom(chosenGamesCountAtom);

  const chooseGame = useAction(chooseGameAction);

  const renderAddToFavoriteButton = (play: Play) => {
    const disabled =
      !play.isChosen && chosenGamesCount >= MAX_FAVORITE_GAMES_COUNT_WITHOUT_VIP && !hasVIP(playerData?.player);

    const checkbox = (
      <label className={chooseButtonStyles}>
        <input
          type="checkbox"
          onChange={() => chooseGame(play.id, !play.isChosen)}
          value={play.id}
          checked={play.isChosen}
          className={chooseGameInputStyles}
          disabled={disabled}
        />
        <Icon
          name="star"
          color={play.isChosen ? 'yellow300' : 'aubergine300'}
          className={play.isChosen ? iconStrokeStyles.active : iconStrokeStyles.default}
          size="27px"
        />
      </label>
    );

    if (disabled) {
      return (
        <Tooltip
          content={t('game-archive.buy-vip-info')}
          background="light"
          placement="right"
          contentClassName={vipInfoStyles}
          closeOnTimeout={false}
        >
          {checkbox}
        </Tooltip>
      );
    }

    return checkbox;
  };

  const renderContent = () => {
    if (!status.isEverSettled && !status.isFulfilled) {
      return (
        <div className={classNames(containerStyles, flexCenterStyles)}>
          <Loader.Mini size="28px" />
        </div>
      );
    }

    if (!games.length) {
      return (
        <div className={classNames(containerStyles, flexCenterStyles)}>
          <Icon name="emptyBox" size={{ width: '150px', height: '155px' }} />
          <span className={noDataTextStyles}>{t('game-archive.no-games')}</span>
        </div>
      );
    }

    return (
      <ul className={classNames(containerStyles, 'scrollStyle')} aria-label="archive">
        {games.map((play) => {
          const enemy = play.whitePlayerId === playerData?.player?.id ? play.blackPlayer : play.whitePlayer;
          const isWinner = play.winner?.playerId === playerData?.player.id;

          const { title } = resultGetterMap[play.winner?.reason!](isWinner);

          return (
            <li key={play.id} className={listItemStyles}>
              {renderAddToFavoriteButton(play)}

              <NextLink href={{ pathname: Routes.gameAnalysis, query: { id: play.id } }} passHref>
                <a className={wrapperStyles}>
                  <div>
                    <div className={modeStyles}>{GAME_MODE_TITLES[play.contextType as GameModeWithTimers]}</div>
                    <div className={infoStyles}>{t('game-archive.with', { name: enemy?.name || '' })}</div>
                  </div>

                  <div className={resultContainerStyles}>
                    <div className={classNames(resultTitleStyles, RESULT_TITLE_CLASSNAME[title as GameResult])}>
                      {title}
                    </div>
                    <div className={dateStyles}>{new Date(play.lastUpdatedAt).toLocaleDateString()}</div>
                  </div>
                </a>
              </NextLink>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      <Modal.Chess
        isOpen
        onRequestClose={onRequestClose}
        header={
          <>
            {t('game-archive.title')}
            <div className={infoIconContainerStyles} onClick={openInfoModal}>
              <Icon name="infoFilled" className={infoIconStyles} size="28" />
            </div>
          </>
        }
        maxWidth={406}
        contentClassName={modalContentStyles}
      >
        {renderContent()}
      </Modal.Chess>

      {isOpenInfoModal && (
        <Modal.Chess
          isOpen
          onRequestClose={closeInfoModa}
          header={t('game-archive.title')}
          contentClassName={infoModalContentStyles}
          maxWidth={406}
        >
          <div className={infoItemStyles}>
            <Icon name="chess" className={iconSizeStyles.chess} />
            <span className={infoItemTextStyles}>{t('game-archive.info.view')}</span>
          </div>
          <div className={infoItemStyles}>
            <Icon name="time" className={iconSizeStyles.time} />
            <span className={infoItemTextStyles}>{t('game-archive.info.add-to-favorites')}</span>
          </div>
          <div className={infoItemStyles}>
            <Icon name="chessWithStar" className={iconSizeStyles.chessWithStar} />
            <span className={infoItemTextStyles}>{t('game-archive.info.analyze')}</span>
          </div>
          <div className={infoItemStyles}>
            <Icon name="chessWithLamp" className={iconSizeStyles.chessWithLamp} />
            <span className={infoItemTextStyles}>{t('game-archive.info.replay')}</span>
          </div>
        </Modal.Chess>
      )}
    </>
  );
};

export default GameArchiveModal;
